import { useEffect, useCallback } from "react";
import {
  Layout,
  Page,
  Card,
  Text,
  BlockStack,
  TextContainer,
  Button,
  ButtonGroup,
  InlineStack,
  EmptyState,
  Link,
  Spinner,
  Badge,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import PrettyDate from "components/format/PrettyDate";
import UploadFile from "features/upload-file/upload-file";
import Files from "features/files/files";
import {
  fetchAuthorizedFileDetails,
  setDestroy,
} from "containers/file-authorized/authorized-file.Slice";
import { RootState, AppDispatch } from "configureStore";
import { useSelector, useDispatch } from "react-redux";
import {
  setModalxComponent,
  setModalxComponentProps,
} from "components/modalx/modalx.slice";
import { API_URL } from "../../constants";
import Loading from "components/loading/Loading";
import CommentsFeature from "components/comment/comments";
import { CommentTypes } from "types/Comment";
import BuildPdf from "components/customer/build-pdf/BuildPdf";
import { hasPdfExtension } from "utils/helperFunctions";
import { formatFileNumberWithSubNumber } from "utils/formatFileNumberDisplay";
import {
  getContactSignersArray,
  renderContactSignerStatuses,
} from "utils/contactSignerTableUtils";
import {
  openDokobitSignUrl,
  createHandleRepeatEmail,
} from "utils/signatureViewUtils";
import type { UserSigner } from "types/DokobitMeta";
import useIsPhone from "features/files/use-is-phone";
import SignerList, { SignerRowData, signerStatusOf } from "containers/file-view-universal/signer-list";
import { isLeaveSubject } from "features/leave-calendar/resolveLeaveTemplateClient";
import DocumentAnnulmentPanel from "components/document-annulment/DocumentAnnulmentPanel";
import {
  resolveKnownUserName,
  canWithdrawDocument as computeCanWithdrawDocument,
} from "utils/signatureUtils";

function AuthorizedFileContainer({ source = "" }) {
  const { t } = useTranslation();
  const { companyId, catId, fileId } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const isPhone = useIsPhone();

  const loadFileData = useCallback(() => {
    if (fileId && companyId) {
      dispatch(fetchAuthorizedFileDetails({ fileId, companyId }));
    }
  }, [fileId, companyId, dispatch]);

  const { fileEntity, isLoading } = useSelector(
    (state: RootState) => state.authorizedFileContainerReducer
  );

  const { role } = useSelector((state: RootState) => state.authReducer);

  useEffect(() => {
    loadFileData();

    return () => {
      dispatch(setDestroy());
    };
  }, [fileId, companyId, loadFileData]);

  const handleOnFileChange = () => {};

  const handleEditFile = (id: string, userId: string) => {
    dispatch(setModalxComponent("UserDocumentForm"));
    dispatch(
      setModalxComponentProps({
        user: userId,
        id,
        subject: "user",
        signType: "Click",
        managerSigner: fileEntity?.user?.companyuser?.manager?.id,
      })
    );
  };

  const handleClickSign = (fileId: string) => {
    dispatch(setModalxComponent("ClickToSignConfirm"));
    dispatch(setModalxComponentProps({ fileId }));
  };

  // check strictly route param and fetch fileId
  const fetchedFileId = fileEntity?.id ?? null;

  if (fetchedFileId !== fileId) {
    return <Loading />;
  }

  if (!fileEntity) {
    return <Loading />;
  }

  const customFieldsContent = fileEntity.customFields
    ?.filter(({ value }) => value)
    .map(({ label, value, fieldType }) => {
      // Dates are always strings, only file fields can be arrays
      if (fieldType === "date") {
        return (
          <Text as="p" variant="bodyMd" key={label}>
            <strong>{t(label)} : </strong>
            <PrettyDate dateString={value as string} />
          </Text>
        );
      } else {
        // For non-date fields, handle arrays (like file fields)
        const displayValue = Array.isArray(value) 
          ? value.join(", ")
          : (value as string);
        return (
          <Text as="p" variant="bodyMd" key={label}>
            <strong>{t(label)} : </strong> {displayValue}
          </Text>
        );
      }
    });

  const categoriesNode = fileEntity?.categories
    ?.map((categoryEntity) => {
      return categoryEntity.title;
    })
    .join(" ,");

  const customersNode = fileEntity?.customer
    ?.map((customerEntity) => {
      return customerEntity.name;
    })
    .join(" ,");

  const handlePdfView = (id: string) => {
    dispatch(setModalxComponent("PdfView"));
    dispatch(
      setModalxComponentProps({
        id,
        companyId,
      })
    );
  };

  const fileUploadedFromDocumentPage = fileEntity.source === "document";
  const isPdf = hasPdfExtension(fileEntity?.title || "");
  // check file is generated by user template
  // only user can edit file if not signed otherwise master can edit by general form
  // manager can do comment only
  // disable child files upload for user generated file
  const isUserTemplate = isLeaveSubject(fileEntity.templateId?.subject || "");
  const isAssetsTemplate = fileEntity.templateId?.subject === "assets";
  const isSigned = fileEntity.isSigned;
  const isUserUploader = fileEntity.user.id === role?.user;
  const isUserMaster = role?.permissionsgroups?.includes("Master");
  // Owner's signing rule: a document generated from a template may never have
  // a signature/signature request removed one by one — only withdrawn whole.
  // (This view has no per-signer cancel button of its own to gate on the
  // template flag directly — only computeCanWithdrawDocument needs it.)
  const isWithdrawn = !!fileEntity.withdrawnAt;
  const canWithdrawDocument = computeCanWithdrawDocument(fileEntity, {
    currentUserId: role?.user,
    isMaster: isUserMaster,
  });
  const withdrawnByName = isWithdrawn
    ? resolveKnownUserName(fileEntity, fileEntity.withdrawnBy)
    : null;

  const ownerSigned = fileEntity?.signersList?.some(
    (signer: any) =>
      signer?.user?.id === fileEntity?.user?.id && signer.status === "signed"
  );

  const sentSignRequestManager = fileEntity?.signersList?.some(
    (signer: any) => signer.position === "manager"
  );

  const handleUploadClick = async (id: string, user: any) => {
    dispatch(setModalxComponent("AddClickSigner"));
    dispatch(
      setModalxComponentProps({
        fileId: id,
        manager: user,
        signType: "Click",
      })
    );
  };

  const handleDeclineSignRequest = async (id: string) => {
    dispatch(setModalxComponent("DeclineClickSignConfirm"));
    dispatch(
      setModalxComponentProps({
        fileId: id,
      })
    );
  };

  const handleDeclineDokobitSignRequest = async (id: string) => {
    dispatch(setModalxComponent("DeclineDokobitSignConfirm"));
    dispatch(
      setModalxComponentProps({
        fileId: id,
      })
    );
  };

  const handleWithdrawClick = () => {
    dispatch(setModalxComponent("WithdrawDocumentConfirm"));
    dispatch(
      setModalxComponentProps({
        fileId: fileEntity.id,
        onDone: () => {
          if (fileId && companyId)
            dispatch(fetchAuthorizedFileDetails({ fileId, companyId }));
        },
      })
    );
  };

  const handleDokobitSign = (fileId: string) =>
    openDokobitSignUrl(companyId!, fileId);

  const handleRepeatEmail = createHandleRepeatEmail(companyId!, () => {
    if (fileId)
      dispatch(fetchAuthorizedFileDetails({ fileId, companyId: companyId! }));
  });

  const handleReviewClick = ({
    id,
    fileId,
    disabled,
  }: {
    id: string;
    fileId: string;
    disabled: boolean;
  }) => {
    if (id && !disabled) {
      dispatch(setModalxComponent("FileReviewConfirmPopup"));
      dispatch(setModalxComponentProps({ id, fileId }));
    }
  };

  const handleDownloadFileVersion = (fileId: string, signatureId: string) => {
    // Open the file version download in a new tab
    window.open(
      `${API_URL}/${companyId}/files/download/${fileId}/signature/${signatureId}`,
      "_blank"
    );
  };

  const dokobitRows: SignerRowData[] = [
    ...(fileEntity?.dokobit?.signersList ?? []).map((signer: any): SignerRowData => ({
      name: signer.name,
      date: signer.signed ? signer.signing_time : fileEntity?.dokobit?.createdAt || "",
      status: signer.signed ? "signed" : signerStatusOf(signer.status),
      declineReason: signer.declineReason,
      actions:
        !signer.signed &&
        signer.status !== "rejected" &&
        !fileEntity.isSigned &&
        !isWithdrawn &&
        signer.id === role?.user ? (
          <>
            <Button onClick={() => handleDokobitSign(fileEntity.id)} accessibilityLabel="Sign">
              {t("sign")}
            </Button>
            <Button onClick={() => handleDeclineDokobitSignRequest(fileEntity.id)} accessibilityLabel="Decline">
              {t("decline")}
            </Button>
          </>
        ) : undefined,
    })),
    ...getContactSignersArray(fileEntity?.dokobit?.contactSigners).map((signer: UserSigner): SignerRowData => ({
      name: signer.name,
      date: signer.signed ? signer.signing_time : fileEntity?.dokobit?.createdAt || "",
      status: signer.status === "withdrawn" ? "withdrawn" : signer.signed ? "signed" : "pending",
      actions:
        !signer.signed &&
        signer.signatureId &&
        signer.status !== "rejected" &&
        !fileEntity.isSigned &&
        !isWithdrawn ? (
          <Button
            onClick={() => signer.signatureId && handleRepeatEmail(signer.signatureId)}
            accessibilityLabel="Repeat email"
          >
            {t("repeat_email")}
          </Button>
        ) : undefined,
    })),
  ];

  const clickRows: SignerRowData[] = (fileEntity?.signersList ?? []).map((signer: any): SignerRowData => {
    const canSignOrDecline = !isWithdrawn && signer.status === "pending" && signer?.user?.id === role?.user;
    return {
      name: signer?.user?.name || "",
      date: signer.status === "signed" ? signer.updatedAt : signer.createdAt,
      status: signerStatusOf(signer.status),
      declineReason: signer?.declineReason,
      actions:
        signer.fileVersionId || canSignOrDecline ? (
          <>
            {signer.fileVersionId && (
              <Button
                variant="plain"
                onClick={() => handleDownloadFileVersion(fileEntity.id, signer.id)}
                accessibilityLabel="Download version"
              >
                {t("download")}
              </Button>
            )}
            {canSignOrDecline && (
              <>
                <Button onClick={() => handleClickSign(fileEntity.id)} accessibilityLabel="Sign">
                  {t("sign")}
                </Button>
                <Button onClick={() => handleDeclineSignRequest(fileEntity.id)} accessibilityLabel="Decline">
                  {t("decline")}
                </Button>
              </>
            )}
          </>
        ) : undefined,
    };
  });

  // The person who opened a document to sign it should not scroll past comments and data
  // first, so on a phone this card is drawn at the top (see the Layout below).
  const signatureCard =
    fileEntity.signType === "Dokobit" && fileEntity.fileId && isPdf ? (
      // if file source is "document"(it means files uploaded by document section) then dokobit signature details should be show
      <Card roundedAbove="sm">
        <InlineStack align="center">{t("document_signature")}</InlineStack>
        <InlineStack align="end">
          <Button variant="plain" onClick={() => handlePdfView(fileEntity.id)} accessibilityLabel="Preview">
            {t("view")}
          </Button>
        </InlineStack>
        {!!fileEntity?.dokobit?.signersList?.length && <SignerList rows={dokobitRows} />}
        <InlineStack align="center">{isSigned && <Badge tone="success">{t("signed")}</Badge>}</InlineStack>
      </Card>
    ) : (isUserTemplate || isAssetsTemplate) && fileEntity.signType === "Click" ? (
      // if document generated by the user and then file uploaded user and manager can click to sign the file
      <Card roundedAbove="sm">
        <InlineStack align="center">{t("document_signature")}</InlineStack>
        <InlineStack align="end">
          <Button variant="plain" onClick={() => handlePdfView(fileEntity.id)} accessibilityLabel="Preview">
            {t("view")}
          </Button>
        </InlineStack>
        <BlockStack gap="400">
          {clickRows.length > 0 && <SignerList rows={clickRows} />}
          {fileEntity.templateId.isApprovalRequired &&
            isUserTemplate &&
            isUserUploader &&
            !isSigned &&
            !isWithdrawn &&
            ownerSigned &&
            !sentSignRequestManager && (
              <InlineStack align="center">
                <Button
                  onClick={() =>
                    handleUploadClick(
                      fileEntity.id,
                      fileEntity?.user?.companyuser?.manager?.id
                    )
                  }
                  accessibilityLabel="Preview"
                >
                  {t("sign_request_to_manager")}
                </Button>
              </InlineStack>
            )}
          <InlineStack align="center">{isSigned && <Badge tone="success">{t("signed")}</Badge>}</InlineStack>
        </BlockStack>
      </Card>
    ) : null;

  return (
    <Page
      fullWidth
      backAction={{
        content: "File-Page",
        url:
          source === "profile"
            ? `/app/${companyId}/profile/${role?.user}/files/${catId}`
            : `/app/${companyId}/documents/${catId}`,
      }}
      title={fileEntity.name}
      titleMetadata={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {isLoading && <Spinner size="small" />}
          {fileEntity.annulledAt && (
            <Badge tone="critical">{t("annulled_badge")}</Badge>
          )}
          {isWithdrawn && <Badge tone="warning">{t("withdrawn_badge")}</Badge>}
        </div>
      }
    >
      <Layout>
        {companyId && fileId && (
          <Layout.Section>
            <DocumentAnnulmentPanel
              companyId={companyId}
              fileId={fileId}
              fileName={fileEntity.name || fileEntity.title}
              annulledAt={fileEntity.annulledAt}
            />
          </Layout.Section>
        )}
        {isPhone && signatureCard && (
          <Layout.Section>
            {signatureCard}
          </Layout.Section>
        )}
        <Layout.Section>
          <BlockStack gap="500">
            <Card roundedAbove="sm">
              {/* The comment box is text-sm, which makes iOS zoom the page when it is focused; its own file is shared, so the fix is applied from here. */}
              <div className="max-md:[&_textarea]:text-base">
                <CommentsFeature id={fileId} CommentTypes={CommentTypes.Files} />
              </div>
            </Card>
            {!isUserTemplate && (
              <Card roundedAbove="sm">
                <div className="flex">
                  <Text as="h2" variant="headingSm">
                    {t("files")}
                  </Text>
                  <div className="flex-grow"></div>
                  <ButtonGroup>
                    <UploadFile
                      onChange={handleOnFileChange}
                      parentFileId={fileId}
                      categoryId={undefined}
                      categoryType={fileEntity.category?.type}
                    />
                    <BuildPdf
                      customerId={fileEntity?.customer[0]?.id}
                      isDisabled={false}
                      templateId={fileEntity.templateId?.id}
                      fileId={fileEntity.id}
                    />
                  </ButtonGroup>
                </div>
                {fileEntity.relatedFiles.length > 0 ? (
                  <Files files={fileEntity.relatedFiles} onChanged={loadFileData} />
                ) : (
                  <EmptyState
                    heading={t("no_files")}
                    image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  >
                    <UploadFile
                      onChange={handleOnFileChange}
                      parentFileId={fileId}
                      categoryId={undefined}
                      categoryType={fileEntity.category?.type}
                    />
                  </EmptyState>
                )}
              </Card>
            )}
          </BlockStack>
        </Layout.Section>
        <Layout.Section variant="oneThird">
          <BlockStack gap="500">
            <Card roundedAbove="sm">
              {/* The gap only matters on a phone, where the tall buttons wrap under each other. */}
              <InlineStack align="space-between" gap={{ xs: "400", md: "0" }}>
                <Link
                  url={`${API_URL}/${companyId}/files/download/${fileEntity.id}`}
                  target="_blank"
                  removeUnderline
                >
                  {t("download")}
                </Link>
                {/* file uploaded user, not signed and (template type === 'user') then form should be open tempalte file edit*/}
                {isUserTemplate && isUserUploader && !isSigned && !isWithdrawn && (
                  <Button
                    variant="plain"
                    onClick={() => {
                      handleEditFile(fileEntity.id, fileEntity.user?.id);
                    }}
                    disabled={isSigned}
                  >
                    {t("edit")}
                  </Button>
                )}
                {canWithdrawDocument && (
                  <Button
                    variant="plain"
                    tone="critical"
                    onClick={handleWithdrawClick}
                  >
                    {t("withdraw_document")}
                  </Button>
                )}
              </InlineStack>
              <TextContainer>
                <Text as="p" variant="bodyMd">
                  <strong>{t("document_name")} : </strong>{" "}
                  {fileEntity?.name || fileEntity.title}
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>{t("customer")} : </strong> {customersNode}
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>{t("category")} : </strong> {categoriesNode}
                </Text>
                {fileEntity.assignedUser?.name && (
                  <Text as="p" variant="bodyMd">
                    <strong>{t("responsible_user")} : </strong>{" "}
                    {fileEntity.assignedUser?.name}
                  </Text>
                )}
                {fileEntity.user?.name && (
                  <Text as="p" variant="bodyMd">
                    <strong>{t("the_file_was_uploaded_by")} : </strong>{" "}
                    {fileEntity.user?.name}
                  </Text>
                )}
                {isWithdrawn && (
                  <Text as="p" variant="bodyMd">
                    <strong>{t("withdrawn_badge")} : </strong>{" "}
                    <PrettyDate
                      dateString={new Date(fileEntity.withdrawnAt as number).toISOString()}
                      showTime={true}
                    />
                    {withdrawnByName && ` — ${withdrawnByName}`}
                    {fileEntity.withdrawReason && (
                      <span className="text-zinc-500">
                        {" "}
                        ({fileEntity.withdrawReason})
                      </span>
                    )}
                  </Text>
                )}
                {!fileEntity.externalNumber ? (
                  <></>
                ) : (
                  <Text as="p" variant="bodyMd">
                    <strong>{t("externalNumber")} : </strong>{" "}
                    {fileEntity.externalNumber}
                  </Text>
                )}
                {fileEntity.series && (
                  <Text as="p" variant="bodyMd">
                    <strong>{t("series")} : </strong> {fileEntity.series.title}
                    {formatFileNumberWithSubNumber(
                      fileEntity.number,
                      fileEntity.subNumber
                    )}
                  </Text>
                )}
                {customFieldsContent}
                <Text as="p" variant="bodyMd">
                  <strong>{t("document_date")} : </strong>{" "}
                  {fileEntity.date ? (
                    <PrettyDate dateString={fileEntity.date} />
                  ) : (
                    "N/A"
                  )}
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>{t("dateTo")} : </strong>{" "}
                  {fileEntity.dateTo ? (
                    <PrettyDate dateString={fileEntity.dateTo} />
                  ) : (
                    "N/A"
                  )}
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>{t("createdAt")} : </strong>{" "}
                  <PrettyDate
                    dateString={fileEntity.createdAt}
                    showTime={true}
                  />
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>{t("updatedAt")} : </strong>{" "}
                  <PrettyDate
                    dateString={fileEntity.updatedAt}
                    showTime={true}
                  />
                </Text>
              </TextContainer>
            </Card>
            <Card roundedAbove="sm">
              <InlineStack align="center">{t("review_by")}</InlineStack>
              {fileEntity.reviewByUsers?.length > 0 ? (
                <div className="content_break content_margin">
                  {fileEntity.reviewByUsers.map((entity, key) => (
                    <Text variant="bodyMd" as="p" key={key}>
                      {t("review_by")} : {entity?.user?.name}{" "}
                      {entity.reviewed ? (
                        <>
                          <PrettyDate
                            dateString={entity.updatedAt}
                            showTime={true}
                          />{" "}
                          <Badge tone="success">{t("reviewed")}</Badge>{" "}
                        </>
                      ) : (
                        <>
                          <PrettyDate
                            dateString={entity?.createdAt || ""}
                            showTime={true}
                          />{" "}
                          <Badge tone="attention">{t("pending")}</Badge>{" "}
                          {role?.user === entity?.user?.id && (
                            <Button
                              onClick={() =>
                                handleReviewClick({
                                  id: entity.id,
                                  fileId: entity.file,
                                  disabled: entity.reviewed,
                                })
                              }
                              accessibilityLabel="Preview"
                            >
                              {t("mark_as_reviewed")}
                            </Button>
                          )}{" "}
                        </>
                      )}
                    </Text>
                  ))}
                </div>
              ) : (
                <>{t("no_review_required")}</>
              )}
            </Card>
            {!isPhone && signatureCard}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default AuthorizedFileContainer;
